import { apiFetch, useMockApi } from '@/lib/api/client'

export interface PromptTemplate {
  id: string
  name: string
  content: string
  createdAt: string
  updatedAt: string
}

export interface PromptTemplatesApi {
  list(): Promise<PromptTemplate[]>
  create(name: string, content: string): Promise<PromptTemplate>
  update(id: string, patch: { name?: string; content?: string }): Promise<PromptTemplate>
  delete(id: string): Promise<void>
}

function mapTemplate(body: Record<string, unknown>): PromptTemplate {
  return {
    id: String(body.id ?? ''),
    name: String(body.name ?? ''),
    content: String(body.content ?? ''),
    createdAt: String(body.created_at ?? ''),
    updatedAt: String(body.updated_at ?? ''),
  }
}

class MockPromptTemplatesApi implements PromptTemplatesApi {
  private items: PromptTemplate[] = [
    {
      id: 'tpl-default',
      name: 'Helpful assistant',
      content: 'You are a helpful, concise assistant.',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  ]

  async list(): Promise<PromptTemplate[]> {
    return structuredClone(this.items)
  }

  async create(name: string, content: string): Promise<PromptTemplate> {
    const now = new Date().toISOString()
    const created: PromptTemplate = {
      id: `tpl-${Date.now()}`,
      name,
      content,
      createdAt: now,
      updatedAt: now,
    }
    this.items.unshift(created)
    return created
  }

  async update(id: string, patch: { name?: string; content?: string }): Promise<PromptTemplate> {
    const index = this.items.findIndex((item) => item.id === id)
    if (index < 0) throw new Error('not_found')
    const updated = {
      ...this.items[index],
      ...patch,
      updatedAt: new Date().toISOString(),
    }
    this.items[index] = updated
    return updated
  }

  async delete(id: string): Promise<void> {
    this.items = this.items.filter((item) => item.id !== id)
  }
}

class HttpPromptTemplatesApi implements PromptTemplatesApi {
  async list(): Promise<PromptTemplate[]> {
    const body = await apiFetch<{ items: Array<Record<string, unknown>> }>('/api/v1/prompt-templates')
    return body.items.map(mapTemplate)
  }

  async create(name: string, content: string): Promise<PromptTemplate> {
    const body = await apiFetch<Record<string, unknown>>('/api/v1/prompt-templates', {
      method: 'POST',
      body: JSON.stringify({ name, content }),
    })
    return mapTemplate(body)
  }

  async update(id: string, patch: { name?: string; content?: string }): Promise<PromptTemplate> {
    const body = await apiFetch<Record<string, unknown>>(`/api/v1/prompt-templates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        name: patch.name,
        content: patch.content,
      }),
    })
    return mapTemplate(body)
  }

  async delete(id: string): Promise<void> {
    await apiFetch<void>(`/api/v1/prompt-templates/${id}`, { method: 'DELETE' })
  }
}

export const promptTemplatesApi: PromptTemplatesApi = useMockApi()
  ? new MockPromptTemplatesApi()
  : new HttpPromptTemplatesApi()
